import { TransportError } from 'ProdantixSdk/core/errors';
import { defaultSleep, type FetchLike, type FetchResponseLike, makeAbortController, resolveFetch } from 'ProdantixSdk/core/http';
import type { Transport, TransportRequest } from 'ProdantixSdk/core/types';

export interface FetchTransportOptions {
  baseDelayMs?: number;
  fetch?: FetchLike;
  maxDelayMs?: number;
  maxRetries?: number;
  random?: () => number;
  requestTimeoutMs?: number;
  sleep?: (ms: number) => Promise<void>;
}

function isPermanent(status: number): boolean {
  return status >= 400 && status < 500 && status !== 429;
}

export class FetchTransport implements Transport {
  private readonly fetch?: FetchLike;
  private readonly maxRetries: number;
  private readonly requestTimeoutMs: number;
  private readonly baseDelayMs: number;
  private readonly maxDelayMs: number;
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly random: () => number;

  constructor(options: FetchTransportOptions = {}) {
    this.fetch = resolveFetch(options.fetch);
    this.maxRetries = options.maxRetries ?? 3;
    this.requestTimeoutMs = options.requestTimeoutMs ?? 10_000;
    this.baseDelayMs = options.baseDelayMs ?? 500;
    this.maxDelayMs = options.maxDelayMs ?? 30_000;
    this.sleep = options.sleep ?? defaultSleep;
    this.random = options.random ?? Math.random;
  }

  async send(request: TransportRequest): Promise<void> {
    const fetchImpl = this.fetch;
    if (!fetchImpl) {
      throw new TransportError('no fetch implementation available in this runtime');
    }
    const payload = JSON.stringify(request.body);
    let lastError: unknown;
    for (let attempt = 0; attempt <= this.maxRetries; attempt += 1) {
      try {
        const response = await this.attempt(fetchImpl, request, payload);
        if (response.ok) {
          return;
        }
        if (isPermanent(response.status)) {
          throw new TransportError(`ingest rejected batch with status ${response.status}`, response.status);
        }
        lastError = new TransportError(`ingest transient status ${response.status}`, response.status);
      } catch (error) {
        if (error instanceof TransportError && error.status !== undefined && isPermanent(error.status)) {
          throw error;
        }
        lastError = error;
      }
      if (attempt < this.maxRetries) {
        await this.sleep(this.backoff(attempt));
      }
    }
    throw new TransportError('ingest delivery failed after retries', undefined, {
      cause: lastError,
    });
  }

  private async attempt(fetchImpl: FetchLike, request: TransportRequest, payload: string): Promise<FetchResponseLike> {
    const controller = makeAbortController();
    const timer = controller ? setTimeout(() => controller.abort(), this.requestTimeoutMs) : undefined;
    try {
      return await fetchImpl(request.url, {
        body: payload,
        headers: {
          authorization: `Bearer ${request.projectKey}`,
          'content-type': 'application/json',
        },
        method: 'POST',
        signal: controller?.signal,
      });
    } finally {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    }
  }

  private backoff(attempt: number): number {
    const exponential = Math.min(this.maxDelayMs, this.baseDelayMs * 2 ** attempt);
    return exponential + this.random() * this.baseDelayMs;
  }
}
